import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CircularProgress from '@mui/material/CircularProgress';
import { useTenantDeploymentStatus } from 'services/v1/TenantMaster/MasterTable/TenantTableDataService';

const ModalContentStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 520,
  bgcolor: 'background.paper',
  borderRadius: 1,
  px: 3,
  py: 2,
};

interface ModalTenantDeploymentProgressProps {
  visible: boolean;
  tenantId: string | null;
  onClose: () => void;
}

function getStatusIcon(deploymentStatus?: string) {
  switch (deploymentStatus) {
    case 'COMPLETED':
      return <CheckCircleOutlineIcon sx={{ color: '#4CAF50', fontSize: 40 }} />;
    case 'FAILED':
      return <ErrorOutlineIcon sx={{ color: '#F44336', fontSize: 40 }} />;
    case 'QUEUED':
      return <HourglassEmptyIcon sx={{ color: '#FF9800', fontSize: 40 }} />;
    default:
      return <CircularProgress size={36} sx={{ color: '#3B4797' }} />;
  }
}

function getStatusColor(deploymentStatus?: string) {
  switch (deploymentStatus) {
    case 'COMPLETED':
      return '#4CAF50';
    case 'FAILED':
      return '#F44336';
    case 'QUEUED':
      return '#FF9800';
    default:
      return '#3B4797';
  }
}

export function ModalTenantDeploymentProgress(props: ModalTenantDeploymentProgressProps) {
  const { visible, tenantId, onClose } = props;
  const { data: deploymentData, isLoading } = useTenantDeploymentStatus(visible ? tenantId : null);

  const status = deploymentData?.data;
  const isFinished = status?.deploymentStatus === 'COMPLETED' || status?.deploymentStatus === 'FAILED';
  const progressPercent =
    status && status.totalSubjects > 0 ? (status.completedSubjects / status.totalSubjects) * 100 : 0;

  return (
    <Modal open={visible}>
      <Stack direction='column' sx={ModalContentStyle}>
        <Stack justifyContent='space-between' direction='row' alignItems='center'>
          <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
            Tenant Deployment Progress
          </Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {isLoading && !status && (
          <Stack alignItems='center' spacing={2} sx={{ py: 4 }}>
            <CircularProgress size={40} sx={{ color: '#3B4797' }} />
            <Typography variant='body2' sx={{ color: '#6B7280' }}>
              Loading deployment status...
            </Typography>
          </Stack>
        )}

        {status && (
          <Stack spacing={2} sx={{ py: 1 }}>
            <Stack direction='row' spacing={2} alignItems='center'>
              {getStatusIcon(status.deploymentStatus)}
              <Stack>
                <Typography variant='body1' fontWeight='bold' sx={{ color: getStatusColor(status.deploymentStatus) }}>
                  {status.deploymentStatus.replace(/_/g, ' ')}
                </Typography>
                <Typography variant='body2' sx={{ color: '#6B7280' }}>
                  {status.message}
                </Typography>
              </Stack>
            </Stack>

            {status.totalSubjects > 0 && (
              <Box sx={{ width: '100%' }}>
                <Stack direction='row' justifyContent='space-between' sx={{ mb: 0.5 }}>
                  <Typography variant='body2' sx={{ color: '#6B7280', fontSize: 12 }}>
                    Progress
                  </Typography>
                  <Typography variant='body2' sx={{ color: '#6B7280', fontSize: 12 }}>
                    {status.completedSubjects} / {status.totalSubjects} subjects
                  </Typography>
                </Stack>
                <LinearProgress
                  variant='determinate'
                  value={progressPercent}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#E3EBF6',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: status.deploymentStatus === 'FAILED' ? '#F44336' : '#4CAF50',
                    },
                  }}
                />
              </Box>
            )}

            {status.error && (
              <Alert severity='error'>
                <Typography variant='body2'>{status.error}</Typography>
              </Alert>
            )}

            {status.startedAt && (
              <Stack spacing={0.5}>
                <Typography variant='body2' sx={{ color: '#6B7280', fontSize: 12 }}>
                  Started: {new Date(status.startedAt).toLocaleString()}
                </Typography>
                {status.finishedAt && (
                  <Typography variant='body2' sx={{ color: '#6B7280', fontSize: 12 }}>
                    Finished: {new Date(status.finishedAt).toLocaleString()}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        )}

        <Divider sx={{ mt: 1, mb: 1 }} />
        <Stack direction='row' justifyContent='center' sx={{ py: 1 }}>
          <Button
            variant='main-table-panel'
            sx={{ width: '100%' }}
            onClick={onClose}
            disabled={!isFinished}
          >
            {isFinished ? 'Close' : 'Deploying...'}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
